import { createRouter, createWebHistory } from "vue-router";
import {
  markRouteLoadingStart,
  markRouteLoadingEnd,
  resetRouteLoading,
  scheduleGlobalLoadingOff,
} from "@/utils/routeLoadingState";
import Home from "@/pages/Home.vue";
const ProductDetails = () => import(/* webpackChunkName: "product-detail" */ "@/pages/ProductDetails.vue");
const ProductSearch = () => import(/* webpackChunkName: "products" */ "@/pages/ProductSearch.vue");
const ShoppingCart = () => import(/* webpackChunkName: "cart" */ "@/pages/ShoppingCart.vue");
const ProductForm = () => import(/* webpackChunkName: "admin-product-form" */ "@/pages/admin/components/ProductForm.vue");
const LoginForm = () => import(/* webpackChunkName: "auth" */ "@/pages/LoginForm.vue");
const RegisterForm = () => import(/* webpackChunkName: "auth" */ "@/pages/RegisterForm.vue");
const PasswordResetPage = () => import(/* webpackChunkName: "auth" */ "@/pages/PasswordResetPage.vue");
const AdminPanel = () => import(/* webpackChunkName: "admin-panel" */ '@/pages/admin/AdminPanel.vue');
const AdminCategories = () => import(/* webpackChunkName: "admin-categories" */ '@/pages/admin/AdminCategories.vue');
const CreateCustomer = () => import(/* webpackChunkName: "admin-customers" */ '@/pages/admin/CreateCustomer.vue');
import MaintenancePage from '@/components/maintenance/MaintenancePage.vue';

// Import the new CarouselManager component
const CarouselManager = () => import('@/pages/admin/CarouselManager.vue')
// Import the new BlogManager component
const BlogManager = () => import('@/pages/admin/BlogManager.vue')
// Import the new BlogEditor components
const BlogEditor = () => import('@/pages/admin/BlogEditor.vue')
// Import the new BlogCategories component
const BlogCategories = () => import('@/pages/admin/BlogCategories.vue')

const routes = [ 
  {
    path: "/mantenimiento",
    name: "Maintenance",
    component: MaintenancePage,
    meta: {
      allowDuringMaintenance: true,
      title: 'Sitio en Mantenimiento'
    }
  },
  {
    path: "/",
    name: "Home",
    component: Home,
  },
  {
    path: "/configuracion",
    component: AdminPanel,
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "UserDashboard",
        component: () => import('@/components/customer/CustomerDashboard.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: "admin",
        name: "AdminOverview",
        component: () => import('@/pages/admin/AdminOverview.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: "ventas",
        name: "AdminSalesHistory",
        component: () => import('@/pages/admin/AdminSalesHistory.vue'),
        meta: { 
          requiresAdmin: true,
          // Añadimos esta propiedad para forzar que el componente siempre se vuelva a cargar
          keepAlive: false 
        }
      },
      {
        path: "stats",
        name: "AdminStats",
        component: () => import('@/pages/admin/AdminStats.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: "ventas/nueva",
        name: "admin-new-sale",
        component: () => import('@/pages/admin/AdminNewSale.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: "productos",
        name: "AdminProductList",
        component: () => import('@/pages/admin/AdminProductList.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: "productos/agregar",
        name: "AddProduct",
        component: ProductForm,
        meta: { requiresAdmin: true }
      },
      {
        path: "productos/editar/:productId",
        name: "ProductEdit",
        component: ProductForm,
        props: true,  
        meta: { requiresAdmin: true }
      },
      {
        path: "actividad",
        name: "AdminActivityPage",
        component: () => import('@/pages/admin/AdminActivity.vue'),
        meta: { requiresAdmin: true }
      },
      {
        path: "my-orders",
        name: "CustomerOrders",
        component: () => import('@/components/customer/CustomerOrders.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: "mi-cuenta",
        name: "UserSettings",
        component: () => import('@/components/settings/UserSettings.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: "categorias",
        name: "admin-categories",
        component: AdminCategories,
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: "resenas",
        name: "admin-reviews",
        component: () => import('@/pages/admin/AdminReviews.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: "clientes",
        name: "admin-customers",
        component: () => import('@/pages/admin/AdminCustomersList.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: '/configuracion/clientes',
        name: 'customers',
        component: () => import('@/pages/admin/AdminCustomersList.vue'),
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: '/configuracion/clientes/crear',
        name: 'create-customer',
        component: CreateCustomer,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: '/configuracion/clientes/:id',
        name: 'customer-profile',
        component: () => import('@/pages/admin/CustomerProfilePage.vue'),
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: '/configuracion/pedidos/:id',
        name: 'order-details',
        component: () => import('@/pages/admin/OrderDetailsPage.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/configuracion/carousel',
        name: 'CarouselManager',
        component: CarouselManager,
        meta: {
          requiresAuth: true,
          adminOnly: true
        }
      },      {
        path: 'blog',
        name: 'BlogManager',
        component: BlogManager,
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'blog/crear',
        name: 'BlogCreate',
        component: BlogEditor,
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'blog/editar/:id',
        name: 'BlogEdit',
        component: BlogEditor,
        props: true,
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'blog/categorias',
        name: 'BlogCategories',
        component: BlogCategories,
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'coupons',
        name: 'AdminCoupons',
        component: () => import('@/pages/admin/AdminCoupons.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'offers',
        name: 'AdminOffers',
        component: () => import('@/pages/admin/AdminOffers.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'scheduled-discounts',
        name: 'AdminScheduledDiscounts',
        component: () => import('@/pages/admin/AdminScheduledDiscounts.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },      {
        path: "mi-tienda",
        name: "StoreSettings",
        component: () => import('@/pages/admin/StoreSettings.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: "reviews",
        name: "ReviewsManager",
        component: () => import('@/pages/admin/ReviewsManager.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      }
    ]
  },
  {
    path: "/producto/:id",
    name: "ProductDetails",
    component: ProductDetails,
  },
  {
    path: "/productos",
    name: "Products",
    component: ProductSearch,
  },
  {
    path: "/blog",
    name: "Blog",
    component: () => import('@/pages/BlogPage.vue'),
  },
  {
    path: "/blog/:slug",
    name: "BlogPost",
    component: () => import('@/pages/BlogPost.vue'),
    props: true,
  },
  {
    path: "/carrito/",
    name: "ShoppingCart",
    component: ShoppingCart,
  },
  {
    path: "/iniciar-sesion",
    name: "LoginForm",
    component: LoginForm,
    meta: { redirectIfAuth: true }
  },
  {
    path: "/crear-cuenta",
    name: "RegisterForm",
    component: RegisterForm,
    meta: { redirectIfAuth: true }
  },
  {
    path: "/recuperar-contrasena",
    name: "PasswordReset",
    component: PasswordResetPage,
    meta: { redirectIfAuth: true }
  },
  {
    path: '/payment/result',
    name: 'PaymentResult',
    component: () => import('@/pages/PaymentResult.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/configuracion/ventas/:id/imprimir',
    name: 'PrintOrder',
    component: () => import('@/pages/admin/PrintOrderPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/"
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

  // SOLUCIÓN CORREGIDA PARA ROUTER GUARD - PRIORIDAD ABSOLUTA PARA ADMINS
router.beforeEach(async (to, from, next) => {
  markRouteLoadingStart(from, to);
  // PASO 1: RUTAS DE ACCESO LIBRE (LOGIN, REGISTER, MANTENIMIENTO)
  // Si estamos yendo a la página de mantenimiento, siempre permitir
  if (to.name === 'Maintenance') {
    return next();
  }

  // Si estamos yendo a la página de login o register, siempre permitir
  if (to.name === 'LoginForm' || to.name === 'RegisterForm') {
    return next();
  }
  
  // PASO 2: VERIFICAR ESTADO DE AUTENTICACIÓN 
  // Verificar autenticación directamente desde localStorage
  const accessToken = localStorage.getItem('accessToken');
  const isAuthenticated = !!accessToken;
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  
  console.log('[Router] Estado de autenticación:', { isAuthenticated, isAdmin });
  
  // PASO 3: VERIFICAR EXPLÍCITAMENTE EL ESTADO DE ADMIN
  if (isAuthenticated) {
    try {
      // Forzar verificación explícita del estado de admin desde el servidor
      const adminService = (await import('@/services/auth')).default;
      const adminStatus = await adminService.isAdmin(true); // Forzar verificación
      
      console.log('[Router] Verificación explícita de admin:', adminStatus);
      
      // ACTUALIZAR estado local basado en la verificación del servidor
      localStorage.setItem('isAdmin', adminStatus ? 'true' : 'false');
      
      // Si es admin, SIEMPRE permitir acceso sin verificar mantenimiento
      if (adminStatus) {
        console.log('[Router] ADMIN VERIFICADO, permitiendo acceso sin restricciones');
        // FORZAR desactivación del modo mantenimiento para administradores
        localStorage.setItem('maintenance_mode', 'false');
        return next(); // Acceso directo sin más verificaciones
      }
    } catch (error) {
      console.error('[Router] Error verificando admin:', error);
    }
  }
  
  // PASO 4: VERIFICACIÓN DE MANTENIMIENTO PARA NO-ADMINS
  try {
    // Siempre permitir el acceso a la página de mantenimiento
    if (to.name === 'Maintenance') {
      return next();
    }
    
    // Importar servicio de mantenimiento
    const maintenanceService = (await import('@/services/maintenanceService')).default;
    
    // Verificar si el sitio está en modo mantenimiento
    const inMaintenance = await maintenanceService.isInMaintenance();
    console.log('[Router] Estado de mantenimiento:', inMaintenance);
    
    if (inMaintenance) {
      // Verificar si es admin o tiene bypass válido
      // IMPORTANTE: Todos los usuarios (autenticados o no) deben redirigirse a mantenimiento
      // a menos que sean admin o tengan bypass validado
      const hasValidBypass = maintenanceService.hasValidatedBypass();
      
      // Si no es bypass permitido, redirigir a página de mantenimiento
      if (!hasValidBypass) {
        console.log('[Router] Modo mantenimiento activo, redirigiendo a /mantenimiento');
        return next({ name: 'Maintenance' });
      } else {
        console.log('[Router] Usuario tiene bypass válido, permitiendo acceso');
      }
    }
  } catch (error) {
    console.error('[Router] Error verificando mantenimiento:', error);
    // En caso de error, continuamos con la navegación normal
  }
  
  // VERIFICACIÓN DE AUTENTICACIÓN Y PERMISOS
  // Si la ruta requiere autenticación y el usuario no está autenticado
  if (to.meta.requiresAuth && !isAuthenticated) {
    console.log('[Router] Redirigiendo a login (requiere autenticación)');
    localStorage.setItem('intendedRoute', to.fullPath);
    return next({
      name: 'LoginForm',
      query: { redirect: to.fullPath }
    });
  }

  // Si la ruta requiere admin y el usuario no es admin
  if (to.meta.requiresAdmin && !isAdmin) {
    console.log('[Router] Redirigiendo a pedidos (requiere admin)');
    return next('/configuracion/my-orders');
  }

  // Si todo está bien, permitir navegación
  next();
});

router.afterEach(() => {
  markRouteLoadingEnd();
  scheduleGlobalLoadingOff();
});

router.onError(() => {
  resetRouteLoading();
});

export default router;